/**
 * User preference helpers — reduce-motion override + action-button
 * size. Mirrors the `lib/sound.ts` localStorage pattern so a fresh tab
 * session inherits whatever the user picked last time.
 *
 * The actual document-level application lives in
 * `applyDocumentPreferences` so a wiring-layer useEffect can keep
 * the DOM in sync with the Zustand store across every render that
 * touches these fields.
 *
 * The felt colour used to live here as a fifth preference. It is not a
 * preference any more — it is the table's own, decided by the stakes —
 * so it moved to `lib/felt_theme.ts` with the rest of that question.
 */

/** Action-button size preference. `"auto"` keeps the standard
 *  56 px / 0.95 rem surface; `"small"` shrinks to 46 px / 0.85 rem
 *  and `"large"` grows to 64 px / 1.05 rem. Drives
 *  `html[data-action-size]`, which remaps the `--action-btn-height`
 *  / `--action-btn-font` tokens the action buttons read. The `.btn`
 *  base class keeps its 44 px HIG tappable floor regardless. */
export type ButtonSizePreference = "auto" | "small" | "large";

const REDUCE_MOTION_STORAGE_KEY = "arkhe.reduce_motion";

/** Auto top-up preference. A client setting, the way every room
 *  carries it (PokerStars: Settings > Buy-In/Rebuy > Auto Rebuy). */
const AUTO_TOP_UP_STORAGE_KEY = "arkhe.table.auto_top_up";
const BUTTON_SIZE_STORAGE_KEY = "arkhe.button_size";

/** Default action-button size — `"auto"` (the standard 56 px
 *  surface) on a fresh tab or when the stored value is garbage. */
export const DEFAULT_BUTTON_SIZE: ButtonSizePreference = "auto";

const VALID_BUTTON_SIZES: ReadonlySet<ButtonSizePreference> = new Set([
  "auto",
  "small",
  "large",
]);

/**
 * Read the user's reduce-motion override. `false` by default so
 * the OS-level `prefers-reduced-motion` media query still wins on
 * a fresh install — the explicit toggle is purely additive (a
 * user who wants motion off but whose OS isn't configured for
 * it can flip the in-app override).
 */
export function readPersistedReduceMotion(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(REDUCE_MOTION_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writePersistedReduceMotion(value: boolean): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(REDUCE_MOTION_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // Storage quota / privacy mode — non-fatal.
  }
}

/**
 * Read the persisted action-button size preference. Falls back to
 * `"auto"` on a fresh tab or when the stored value is garbage.
 */
export function readPersistedButtonSize(): ButtonSizePreference {
  try {
    if (typeof localStorage === "undefined") return DEFAULT_BUTTON_SIZE;
    const raw = localStorage.getItem(BUTTON_SIZE_STORAGE_KEY);
    if (raw === null) return DEFAULT_BUTTON_SIZE;
    return VALID_BUTTON_SIZES.has(raw as ButtonSizePreference)
      ? (raw as ButtonSizePreference)
      : DEFAULT_BUTTON_SIZE;
  } catch {
    return DEFAULT_BUTTON_SIZE;
  }
}

export function writePersistedButtonSize(size: ButtonSizePreference): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(BUTTON_SIZE_STORAGE_KEY, size);
  } catch {
    // Storage quota / privacy mode — non-fatal.
  }
}

/**
 * Apply the reduce-motion + button-size flags to the document root:
 *   - `data-reduce-motion="true"` (omitted when off)
 *   - `data-action-size="auto|small|large"`
 *
 * The felt is applied separately (`lib/felt_theme.ts::applyFeltTheme`)
 * because it answers to the table rather than to the reader.
 *
 * Light / dark theming retired — the dark palette is the single
 * canonical look (matches the PokerStars / GGPoker / partypoker /
 * 888 evening-felt idiom). The OS-level `prefers-color-scheme`
 * hint is intentionally ignored.
 */
export function applyDocumentPreferences(
  reduceMotion: boolean,
  buttonSize: ButtonSizePreference = DEFAULT_BUTTON_SIZE,
  doc: Document | undefined = typeof document !== "undefined"
    ? document
    : undefined,
): void {
  if (!doc) return;
  const root = doc.documentElement;
  if (reduceMotion) {
    root.setAttribute("data-reduce-motion", "true");
  } else {
    root.removeAttribute("data-reduce-motion");
  }
  root.setAttribute("data-action-size", buttonSize);
}

/**
 * Keep my seat topped up automatically.
 *
 * The window between hands is a few seconds — 2.5 after a fold-win,
 * 6 after a showdown, plus the shuffle — and a player who has just lost
 * a pot has to notice, open a menu, pick an amount and submit inside
 * it. Every room answers this the same way, with a setting that does it
 * for you after each hand ("Auto Rebuy" on PokerStars and GGPoker,
 * "Auto Top Up" elsewhere), which is why the manual door alone is not
 * the standard.
 *
 * Off by default: it spends from the wallet without asking again, so it
 * is opted into, never assumed.
 */
export function readPersistedAutoTopUp(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(AUTO_TOP_UP_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writePersistedAutoTopUp(value: boolean): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(AUTO_TOP_UP_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // Storage quota / privacy mode — non-fatal.
  }
}
